import { html } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_PATCH_MANAGEMENT_MAINTENANCE_WINDOWS_CONTAINER } from '../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { property } from 'lit/decorators.js';
import { SelectOption } from '../../../../../../../../ui/gzDropdown/types';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { classMap } from 'lit/directives/class-map.js';
import { PatchManagementAvailableMaintenanceWindow } from '../../../../../utils/uiSettings/patchManagement/patch-management.types';
import { FormFieldInput } from '../../../../../../../../ui/gzForm/formField/component/FormFieldInput';
import { areArraysEqual } from '@nirvana/shared/basic-types/object';
import {
  convertAvailableMaintenanceWindowsToDropdownOptions,
  convertAvailableMaintenanceWindowsToSelectedMaintenanceWindows,
} from '../../../../../utils/uiSettings/patchManagement/patch-management.values';
import { OnAddWindowClick, OnMaintenanceWindowOptionSelect } from '../../events/events';
import { PatchManagementMaintenanceWindow } from '@nirvana/shared/dtos/types/policies/common';

@customElement(WEB_COMPONENT_POLICY_PANEL_PATCH_MANAGEMENT_MAINTENANCE_WINDOWS_CONTAINER)
export class GzMaintenanceWindowsContainerWebComponent extends FormFieldInput<PatchManagementMaintenanceWindow[]> {
  @property({ type: Boolean, attribute: true, reflect: true })
  isError = false;

  /**
   * Property used for setting the selected items in the dropdown.
   */
  @property({ type: Array, attribute: true, reflect: true })
  public selectedMaintenanceWindows: PatchManagementMaintenanceWindow[] = [];

  /**
   * Property used for setting the options in the dropdown.
   */
  @property({ type: Array, attribute: true, reflect: true })
  public availableMaintenanceWindows: PatchManagementAvailableMaintenanceWindow[] = [];

  @query('#patch-management-general-maintenance-windows-form-field')
  private maintenanceWindowsFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  // Constant used for the no selected maintenance window option.
  private readonly noSelectedMaintenanceWindowOption = {
    value: 'N/A', // use 'N/A' as value to avoid conflicts with other values
    title: translateFn('No maintenance window selected'),
  };

  /**
   * Styles for the component.
   */
  static get styles() {
    return styles;
  }

  /**
   * Get form field value as an array of objects with id and name fields.
   */
  getFormValue(): PatchManagementMaintenanceWindow[] {
    // return received maintenance windows if error
    if (this.isError) {
      return this.selectedMaintenanceWindows;
    }

    // filter by dropdown value
    return this.availableMaintenanceWindows
      .filter(mw => mw._id === this.maintenanceWindowsFormField.value)
      .map(mw => convertAvailableMaintenanceWindowsToSelectedMaintenanceWindows(mw));
  }

  /**
   * Set form field value.
   * @param value
   */
  setFormValue(value: PatchManagementMaintenanceWindow[]): void {
    this.selectedMaintenanceWindows = value;
  }

  /**
   * Render specific content.
   */
  protected render() {
    this.updateComplete.then(() => {
      // set error state for form field
      this.maintenanceWindowsFormField.Input.isError = this.isError;
      if (this.isError) {
        return;
      }

      // set selected options in dropdown
      this.maintenanceWindowsFormField.value = this.getSelectedMaintenanceWindowId();
    });

    // get classes for hiding components
    const hasAvailableWindows = this.availableMaintenanceWindows.length > 0 || this.isError;
    const noAvailableWindowsClasses = {
      hidden: hasAvailableWindows,
    };
    const availableWindowsClasses = {
      hidden: !hasAvailableWindows,
    };

    return html`
      <div class="maintenance-windows-container">
        <gz-form-field id="patch-management-general-maintenance-windows-form-field" ?disabled="${this.readonly}">
          <gz-dropdown
            gz-form-input
            type="dropdown"
            id="patch-management-general-maintenance-windows-dropdown"
            label="${translateFn('Maintenance windows')}"
            noResultsFound="${translateFn('No maintenance window defined')}"
            placeholder="${translateFn('No maintenance window defined')}"
            errorMessage="${translateFn('Could not load maintenance window list')}"
            .options="${this.getMaintenanceWindowOptions()}"
            @optionSelected="${this.onMaintenanceWindowOptionSelected}"
          ></gz-dropdown>
          <gz-input-error></gz-input-error>
        </gz-form-field>
        <gz-button
          class="${classMap(noAvailableWindowsClasses)}"
          id="create-window-button"
          type="outline"
          @click="${this.onAddWindowClick}"
          ?disabled="${this.readonly}"
          >${translateFn('Create window')}</gz-button
        >
      </div>

      <gz-text
        class="${classMap(availableWindowsClasses)}"
        id="patch-management-general-info-text"
        fontSize="12"
        fontWeight="400"
        class="infoText"
        >${translateFn(
          'Select a maintenance window for this policy. Maintenance windows are defined in Policies > Configuration Profiles.'
        )}</gz-text
      >

      <gz-info-box class="${classMap(noAvailableWindowsClasses)}" severity="warning" id="rule-sets-notice">
        ${translateFn(
          'No maintenance window available. Click to create a window with basic settings. You can edit the window later in Policies > Configuration Profiles.'
        )}
      </gz-info-box>
    `;
  }

  /**
   * Get maintenance window options.
   */
  private getMaintenanceWindowOptions(): SelectOption[] {
    // no available maintenance windows, we need to show Create window button
    if (!this.availableMaintenanceWindows.length) {
      return [];
    }

    // add no selected maintenance window option
    return [
      this.noSelectedMaintenanceWindowOption,
      ...convertAvailableMaintenanceWindowsToDropdownOptions(this.availableMaintenanceWindows),
    ];
  }

  /**
   * Get selected maintenance window id.
   */
  private getSelectedMaintenanceWindowId(): string {
    // no available maintenance windows, we need to keep the dropdown empty
    if (!this.availableMaintenanceWindows.length) {
      return '';
    }

    // no selected maintenance window, we need to show the no selected maintenance window option
    if (!this.selectedMaintenanceWindows.length) {
      return this.noSelectedMaintenanceWindowOption.value;
    }

    // return the first selected maintenance window id
    return this.selectedMaintenanceWindows[0].id;
  }

  /**
   * Dispatches OnMaintenanceWindowOptionSelect event.
   * @param event
   */
  private onMaintenanceWindowOptionSelected(event: CustomEvent) {
    // convert 'N/A' value to empty string
    let oldValue = event.detail.oldValue;
    if (event.detail.oldValue && event.detail.oldValue.value === 'N/A') {
      oldValue = { ...oldValue, value: '' };
    }

    // convert 'N/A' value to empty string
    let newValue = event.detail.newValue;
    if (event.detail.newValue && event.detail.newValue.value === 'N/A') {
      newValue = { ...newValue, value: '' };
    }

    this.dispatchEvent(new OnMaintenanceWindowOptionSelect(oldValue, newValue));
  }

  /**
   * Dispatches OnAddWindowClick event.
   */
  private onAddWindowClick() {
    this.dispatchEvent(new OnAddWindowClick());
  }

  /**
   * Resets component properties.
   */
  public reset() {
    this.selectedMaintenanceWindows = [];
    this.availableMaintenanceWindows = [];
  }

  /**
   * Overrides FormFieldInput method for comparing two arrays of objects
   * (this component uses array as form field value)
   */
  public areFormValuesEqual(value1: PatchManagementMaintenanceWindow[], value2: PatchManagementMaintenanceWindow[]) {
    return areArraysEqual(
      value1.map(val => val.id),
      value2.map(val => val.id)
    );
  }
}
